import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Wallet,
  Briefcase,
  Receipt,
  Database,
  Package,
  ClipboardCheck,
  Users,
  Activity,
  Sun,
  Moon,
  ShieldCheck,
  Wrench,
  Boxes,
} from "lucide-react";
import { toast } from "sonner";

import { authApi } from "@/lib/adminApi";
import { normalizeRole } from "@/components/admin/AdminRoute";

export default function Sidebar({
  active,
  onSelect,
  theme,
  onToggleTheme,
  counts = {},
  activeRoute,
  onClose,
  mobileOverlay = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const currentUser = authApi.getCachedUser();
  const userRole = normalizeRole(currentUser?.role);
  const isAdmin = userRole === "ADMIN";

  const onHome =
    location.pathname === "/" || activeRoute === "dashboard";

  const onExpenses =
    location.pathname === "/expenses" || activeRoute === "expenses";

  const onHrms =
    location.pathname.startsWith("/hrms") || activeRoute === "hrms";

  const onBilling =
    location.pathname === "/billing" || activeRoute === "billing";

  const onDiscom =
    location.pathname.startsWith("/discom") || activeRoute === "discom";

  const onInstallationOperations =
    location.pathname.startsWith("/installation-operations") ||
    activeRoute === "installation-operations";

  const onMaterialInventory =
    location.pathname.startsWith("/inventory") ||
    activeRoute === "material-inventory";

  const onDt =
    location.pathname.startsWith("/dt-inspection") ||
    activeRoute === "dt-inspection";

  const onMasterData =
    location.pathname.startsWith("/admin/resources") ||
    activeRoute === "master-data";

  const onUserManagement =
    location.pathname.startsWith("/admin/users") ||
    activeRoute === "user-management";

  const onActivityLog =
    location.pathname.startsWith("/admin/activity") ||
    activeRoute === "activity-log";

  const goHome = () => {
    if (onHome && onSelect) {
      onSelect("All");
    } else {
      navigate("/", { state: { category: "All" } });
    }

    onClose?.();
  };

  const goExpenses = () => {
    navigate("/expenses");
    onClose?.();
  };

  const goHrms = () => {
    navigate("/hrms");
    onClose?.();
  };

  const goBilling = () => {
    navigate("/billing");
    onClose?.();
  };

  const goDiscom = () => {
    navigate("/discom");
    onClose?.();
  };

  /*
   * NEW MODULE:
   * Installation & Operations
   */
  const goInstallationOperations = () => {
    navigate("/installation-operations");
    onClose?.();
  };

  /*
   * NEW MODULE:
   * Material Inventory
   *
   * Existing /inventory route is intentionally retained.
   * Existing data is NOT deleted.
   */
  const goMaterialInventory = () => {
    navigate("/inventory");
    onClose?.();
  };

  const goDtInspection = () => {
    navigate("/dt-inspection");
    onClose?.();
  };

  /*
   * These three modules are visible to EVERYONE.
   * Only ADMIN can actually open them.
   */
  const requireAdmin = (route, moduleName) => {
    if (!isAdmin) {
      toast.error(`${moduleName} is restricted`, {
        description: "Only ADMIN users can access this module.",
      });
      return;
    }

    navigate(route);
    onClose?.();
  };

  const goMasterData = () => {
    requireAdmin("/admin/resources", "Master Data");
  };

  const goUserManagement = () => {
    requireAdmin("/admin/users", "User Management");
  };

  const goActivityLog = () => {
    requireAdmin("/admin/activity", "Activity Log");
  };

  const navItemClass = (isActive) =>
    `w-full group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
      isActive
        ? "bg-foreground text-background"
        : "text-foreground/70 hover:text-foreground hover:bg-muted"
    }`;

  const activeDot = (isActive) =>
    isActive ? (
      <motion.div
        layoutId="sidebar-active-dot"
        className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--energy))]"
      />
    ) : null;

  return (
    <aside
      data-testid="sidebar"
      className={`${
        mobileOverlay ? "flex" : "hidden lg:flex"
      } flex-col w-[280px] shrink-0 border-r border-border bg-background ${
        mobileOverlay ? "h-full" : "sticky top-0 h-screen"
      }`}
    >
      {/* Brand */}
      <button
        onClick={() => {
          navigate("/");
          onClose?.();
        }}
        className="px-6 pt-7 pb-8 text-left hover:opacity-90 transition-opacity"
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 blur-lg bg-[hsl(var(--primary))]/40 rounded-lg" />

            <div className="relative w-11 h-11 rounded-lg bg-black flex items-center justify-center overflow-hidden shadow-md">
              <img
                src="/pps-logo.png"
                alt="Prathvi Power Solutions"
                className="w-full h-full object-contain p-0.5"
              />
            </div>
          </div>

          <div>
            <div className="font-heading text-base font-black leading-tight">
              Prathvi Power
              <br />
              Solutions
            </div>
          </div>
        </div>
      </button>

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto no-scrollbar">
        {/* Modules heading */}
        <div className="px-3 pb-2 text-[10px] font-bold tracking-[0.2em] text-muted-foreground uppercase">
          Modules
        </div>

        {/* HOME */}
        <button
          data-testid="nav-home"
          onClick={goHome}
          className={navItemClass(onHome && active === "All")}
        >
          <LayoutDashboard className="w-4 h-4 shrink-0" />

          <span className="flex-1 text-left">Home</span>

          {activeDot(onHome && active === "All")}
        </button>

        {/* INSTALLATION & OPERATIONS */}
        <button
          data-testid="nav-installation-operations"
          onClick={goInstallationOperations}
          className={navItemClass(onInstallationOperations)}
        >
          <Wrench className="w-4 h-4 shrink-0" />

          <span className="flex-1 text-left">
            Installation &amp; Operations
          </span>

          {activeDot(onInstallationOperations)}
        </button>

        {/* MATERIAL INVENTORY */}
        <button
          data-testid="nav-material-inventory"
          onClick={goMaterialInventory}
          className={navItemClass(onMaterialInventory)}
        >
          <Boxes className="w-4 h-4 shrink-0" />

          <span className="flex-1 text-left">
            Material Inventory
          </span>

          {activeDot(onMaterialInventory)}
        </button>

        {/* DAILY EXPENSES */}
        <button
          data-testid="nav-daily-expenses"
          onClick={goExpenses}
          className={navItemClass(onExpenses)}
        >
          <Wallet className="w-4 h-4 shrink-0" />

          <span className="flex-1 text-left">
            Daily Expenses
          </span>

          {activeDot(onExpenses)}
        </button>

        {/* HRMS & PAYROLL */}
        <button
          data-testid="nav-hrms"
          onClick={goHrms}
          className={navItemClass(onHrms)}
        >
          <Briefcase className="w-4 h-4 shrink-0" />

          <span className="flex-1 text-left">
            HRMS &amp; Payroll
          </span>

          {activeDot(onHrms)}
        </button>

        {/* VENDOR BILLING */}
        <button
          data-testid="nav-billing"
          onClick={goBilling}
          className={navItemClass(onBilling)}
        >
          <Receipt className="w-4 h-4 shrink-0" />

          <span className="flex-1 text-left">
            Vendor Billing
          </span>

          {activeDot(onBilling)}
        </button>

        {/* DISCOM */}
        <button
          data-testid="nav-discom"
          onClick={goDiscom}
          className={navItemClass(onDiscom)}
        >
          <Database className="w-4 h-4 shrink-0" />

          <span className="flex-1 text-left">
            DISCOM
          </span>

          {activeDot(onDiscom)}
        </button>

        {/* DT INSPECTION */}
        <button
          data-testid="nav-dt"
          onClick={goDtInspection}
          className={navItemClass(onDt)}
        >
          <ClipboardCheck className="w-4 h-4 shrink-0" />

          <span className="flex-1 text-left">
            DT Inspection
          </span>

          {activeDot(onDt)}
        </button>

        {/* MASTER DATA */}
        <button
          data-testid="nav-master-data"
          onClick={goMasterData}
          className={navItemClass(onMasterData)}
        >
          <Database className="w-4 h-4 shrink-0" />

          <span className="flex-1 text-left">
            Master Data
          </span>

          {!isAdmin && (
            <ShieldCheck className="w-3.5 h-3.5 shrink-0 opacity-50" />
          )}

          {activeDot(onMasterData)}
        </button>

        {/* USER MANAGEMENT */}
        <button
          data-testid="nav-user-management"
          onClick={goUserManagement}
          className={navItemClass(onUserManagement)}
        >
          <Users className="w-4 h-4 shrink-0" />

          <span className="flex-1 text-left">
            User Management
          </span>

          {!isAdmin && (
            <ShieldCheck className="w-3.5 h-3.5 shrink-0 opacity-50" />
          )}

          {activeDot(onUserManagement)}
        </button>

        {/* ACTIVITY LOG */}
        <button
          data-testid="nav-activity-log"
          onClick={goActivityLog}
          className={navItemClass(onActivityLog)}
        >
          <Activity className="w-4 h-4 shrink-0" />

          <span className="flex-1 text-left">
            Activity Log
          </span>

          {!isAdmin && (
            <ShieldCheck className="w-3.5 h-3.5 shrink-0 opacity-50" />
          )}

          {activeDot(onActivityLog)}
        </button>
      </nav>

      {/* Footer / Theme toggle */}
      <div className="p-4 border-t border-border">
        <button
          data-testid="theme-toggle"
          onClick={onToggleTheme}
          className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-muted hover:bg-secondary transition-colors"
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            {theme === "dark" ? (
              <Moon className="w-4 h-4" />
            ) : (
              <Sun className="w-4 h-4" />
            )}

            {theme === "dark" ? "Dark Mode" : "Light Mode"}
          </span>

          <span
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              theme === "dark"
                ? "bg-foreground"
                : "bg-muted-foreground/20"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-background shadow transition-transform ${
                theme === "dark"
                  ? "translate-x-4"
                  : "translate-x-0.5"
              }`}
            />
          </span>
        </button>

        <div className="mt-3 px-3 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--energy))]" />
          All systems online
        </div>
      </div>
    </aside>
  );
}